// Copyright (c) 2025 UXtechLab. All Rights Reserved.
// Originally created by @taekie. Licensed under BUSL-1.1 by UXtechLab. See LICENSE for details.
/**
 * Nesting For Voronoi Utility
 *
 * Transforms flat data into a nested hierarchical structure suitable
 * for d3.hierarchy() and voronoi treemap visualization.
 *
 * Creates a 3-level hierarchy: root -> metaLabel -> label -> text
 * Each leaf node contains size values for sizing and references to original data.
 */

import d3 from './utils/d3-bundle.js';

/**
 * Convert flat data array into nested hierarchy structure for voronoi treemap
 *
 * @param {Object[]} data - Array of data objects with metaLabel, label, text, and bubbleSize fields
 * @param {string} [key1='label'] - Field name for first level grouping (label)
 * @param {string} [key2='text'] - Field name for second level grouping (text)
 * @returns {Object} Nested hierarchy object with key/values structure for d3.hierarchy
 *
 * @example
 * const data = [
 *   { metaLabel: 'A', label: 'Group1', text: 'Item1', bubbleSize: 10 },
 *   { metaLabel: 'A', label: 'Group1', text: 'Item2', bubbleSize: 20 }
 * ];
 * const nested = nestingForVoronoi(data);
 * const hierarchy = d3.hierarchy(nested, d => d.values).sum(d => d.size);
 */
export function nestingForVoronoi(
  data,
  key1 = "label",
  key2 = "text"
) {
  // 1. Extract only necessary fields
  const simpleData = data.map((d) => ({
    [key1]: d[key1],
    [key2]: d[key2],
    metaLabel: d.metaLabel,
    size: d.bubbleSize ?? 1
  }));

  // 2. 3-level grouping with d3.rollups: metaLabel -> key1 -> key2
  const nested = d3.rollups(
    simpleData,
    (d) => d3.sum(d.map((v) => v.size)),
    (d) => d.metaLabel,
    (d) => d[key1],
    (d) => d[key2]
  );

  // 3. Helper to convert to dictionary format
  const makeDictionary = (bc, bcData, metaLabel) => {
    return bcData.map((k) => {
      const grouping = {
        [key1]: bc,
        [key2]: k[0],
        size: k[1] ? k[1] : 1
      };

      const originalData = data.filter(
        (c) =>
          c.metaLabel === metaLabel &&
          c[key1] === grouping[key1] &&
          c[key2] === grouping[key2]
      );

      // Keep all original fields on the leaf item (grouping keys + size win),
      // so popups can reference any data field via {field} with no extra wiring.
      const item = { ...(originalData[0] || {}), ...grouping };

      return {
        key: k[0],
        values: [item],
        data: originalData[0],
        raw: originalData
      };
    });
  };

  // 4. Generate final hierarchical structure
  const kv = nested.map(([metaLabel, metaLabelData]) => ({
    key: metaLabel,
    values: metaLabelData.map(([bc, bcData]) => ({
      key: bc,
      values: makeDictionary(bc, bcData, metaLabel)
    }))
  }));

  return {
    key: "root_nest",
    values: kv.filter((d) => d.key)
  };
}

export default nestingForVoronoi;
